import {SHOT_ANIMATION, SHOT_KINDS, SHOT_VOLLEY_MAX_PROJECTILES} from '../../configs/shotAnimation';

/**
 * Описание залпа: набор снарядов с готовыми точками вылета и попадания,
 * высотой дуги и таймингами. Чистая функция без Phaser и DOM — по этому
 * описанию рисует HexGridRenderer.showShotAnimation.
 *
 * Поле видно сверху, поэтому «высоту» изображают две вещи разом: снаряд
 * уходит вверх по экрану (на высоту дуги над линией земли) и крупнеет к
 * верхней точке. Точка на земле под снарядом идет от стрелка к цели по
 * прямой — по ней ползет тень валуна.
 *
 * СНАРЯДОВ В ЗАЛПЕ — ПО РАЗМЕРУ ОТРЯДА: и у эльфов (стрелы по пологой дуге),
 * и у катапульт (валуны навесом). Стреляет отряд, а не один стрелок, поэтому
 * у залпа разброс: снаряды уходят один за другим с неровным шагом, из разных
 * точек вокруг стрелка в разные точки вокруг цели, каждый по своей дуге.
 * Одиночный снаряд (отряд размера 1) летит точно из центра в центр.
 *
 * Попаданием залпа считается ПОСЛЕДНИЙ снаряд: к этому моменту на поле
 * появляется итог выстрела. Его время не плавает — по нему считаются звук и
 * сжатие залпа под шаг темпа.
 *
 * В состояние партии случайность не попадает: эффект целиком визуальный, у
 * каждого клиента свой (детерминизм записей матчей не задет).
 */

const randomRange = (rng, min, max) => min + rng() * (max - min);

// Значение, растущее с дальностью и ограниченное сверху
const scaleByDistance = ({base, perHex, max}, distanceHexes) => Math.min(max, base + perHex * distanceHexes);

/** Сколько снарядов в залпе отряда такого размера: по одному на единицу размера */
export const getShotProjectileCount = (unitSize) => {
  const size = Math.floor(Number(unitSize));
  if (!Number.isFinite(size) || size < 1) {
    return 1;
  }
  return Math.min(SHOT_VOLLEY_MAX_PROJECTILES, size);
};

/**
 * @param {Object} params
 * @param {string} params.kind — SHOT_KINDS
 * @param {{x: number, y: number}} params.from — стрелок (на высоте отряда)
 * @param {{x: number, y: number}} params.to — цель (на высоте отряда)
 * @param {number} params.hexSize — размер ячейки: от него считаются все размеры
 * @param {number} [params.unitSize] — размер стреляющего отряда: столько
 *   снарядов в залпе
 * @param {Function} [params.rng] — источник случайности (в тестах — заглушка)
 * @returns {{projectiles: Object[], impactAtMs: number}} impactAtMs — когда
 *   залп попадает в цель (падает последний снаряд).
 *   delay — от начала залпа; последний снаряд помечен isImpact. У валунов еще
 *   sizeMultiplier (свой размер камня), spinTurns (обороты за полет) и
 *   variantSeed (0..1 — какая из картинок валуна)
 */
export const buildShotVolley = ({kind, from, to, hexSize, unitSize, rng = Math.random} = {}) => {
  if (!from || !to || !hexSize || hexSize <= 0) {
    return {projectiles: [], impactAtMs: 0};
  }

  const isBoulder = kind !== SHOT_KINDS.ARROWS;
  const config = isBoulder ? SHOT_ANIMATION.boulder : SHOT_ANIMATION.arrows;
  const count = getShotProjectileCount(unitSize);
  const isVolley = count > 1;

  const distancePx = Math.hypot(to.x - from.x, to.y - from.y);
  // Шаг между центрами соседних ячеек — корень из трех размеров ячейки
  const distanceHexes = distancePx / (hexSize * Math.sqrt(3));
  const flightMs = Math.round(scaleByDistance(config.flightMs, distanceHexes));
  const arcHeight = hexSize * scaleByDistance(config.arcHeightHexes, distanceHexes);

  // Шаг между снарядами: большой залп укладывается в окно, шаг сжимается
  const staggerMs = isVolley ? Math.min(config.staggerMs, config.staggerWindowMs / (count - 1)) : 0;
  const windowMs = staggerMs * (count - 1);
  const windupMs = config.windupMs || 0;
  // Стрелы летят одинаково долго: залп кончается через окно вылета и полет.
  // Валуны летят быстрее ровно на окно вылета: последний падает тогда же,
  // когда упал бы одиночный, — удар должен попасть в звук
  const baseDuration = isBoulder ? flightMs - windowMs : flightMs;
  const lastLandsAtMs = windupMs + windowMs + baseDuration;

  // Точка в круге заданного радиуса вокруг центра
  const scatter = (center, radius) => {
    const angle = randomRange(rng, 0, Math.PI * 2);
    const distance = radius * Math.sqrt(rng());
    return {x: center.x + Math.cos(angle) * distance, y: center.y + Math.sin(angle) * distance};
  };

  const projectiles = [];
  for (let index = 0; index < count; index += 1) {
    const isLast = index === count - 1;
    // Первый вылет и последнее попадание не плавают: по ним идут звук и итог
    const launchJitterMs = index === 0 || isLast
      ? 0
      : randomRange(rng, -1, 1) * staggerMs * config.launchJitterShare;
    const delay = windupMs + index * staggerMs + launchJitterMs;

    let duration = baseDuration;
    if (isBoulder && isVolley && !isLast) {
      duration = baseDuration * (1 + randomRange(rng, -1, 1) * config.durationJitter);
      // Никто не падает позже последнего: попадание залпа — именно он
      duration = Math.min(duration, lastLandsAtMs - staggerMs * 0.25 - delay);
    }

    const start = isVolley ? scatter(from, hexSize * config.startSpreadFactor) : from;
    const end = isVolley ? scatter(to, hexSize * config.endSpreadFactor) : to;
    const projectile = {
      index,
      delay: Math.round(delay),
      duration: Math.round(duration),
      startX: start.x,
      startY: start.y,
      endX: end.x,
      endY: end.y,
      // У каждого снаряда залпа дуга чуть своя: залп не идет одной линией
      arcHeight: arcHeight * (isVolley ? randomRange(rng, config.arcJitter.min, config.arcJitter.max) : 1),
      isImpact: isLast,
    };
    if (isBoulder) {
      projectile.sizeMultiplier = isVolley ? randomRange(rng, config.sizeJitter.min, config.sizeJitter.max) : 1;
      projectile.spinTurns = randomRange(rng, config.spinTurns.min, config.spinTurns.max);
      projectile.variantSeed = rng();
    }
    projectiles.push(projectile);
  }

  const impact = projectiles[projectiles.length - 1];
  const impactAtMs = impact.delay + impact.duration;
  return {projectiles, impactAtMs};
};

/**
 * Во сколько раз сжать залп по времени, чтобы он уложился в шаг темпа.
 *
 * Чужие действия в живой партии идут с известным шагом (боты), и следующим
 * может прийти завершение этапа: выстрелы применяются, итог с поля уходит.
 * Итог же появляется только с попаданием — значит, попасть надо раньше, чем
 * кончится шаг, и оставить итогу его долю шага (pace.resultHoldShare).
 *
 * @param {Object} params
 * @param {number} params.impactAtMs — когда залп попадает в цель при обычной
 *   длительности, считая от экшена (с паузой до пуска)
 * @param {number} [params.paceMs] — шаг темпа; не задан — темп свой (стреляет
 *   сам игрок), сжимать нечего
 * @returns {number} множитель длительностей: 1 — как есть, меньше — быстрее,
 *   0 — в шаг не уложиться, анимации не будет
 */
export const resolveShotTimeFactor = ({impactAtMs, paceMs} = {}) => {
  if (!Number.isFinite(paceMs) || paceMs <= 0 || !(impactAtMs > 0)) {
    return 1;
  }

  const {resultHoldShare, minTimeFactor} = SHOT_ANIMATION.pace;
  const timeFactor = Math.min(1, paceMs * (1 - resultHoldShare) / impactAtMs);
  return timeFactor < minTimeFactor ? 0 : timeFactor;
};

/**
 * Положение снаряда на дуге.
 *
 * @param {Object} projectile — из buildShotVolley
 * @param {number} progress — ход полета от 0 до 1
 * @returns {{groundX: number, groundY: number, x: number, y: number,
 *   lift: number, angleRad: number}} groundX/groundY — точка на земле под
 *   снарядом (там тень), x/y — сам снаряд, lift — доля высоты от 0 (на земле)
 *   до 1 (верхняя точка), angleRad — куда снаряд смотрит по касательной к дуге
 */
export const resolveShotProjectilePosition = (projectile, progress) => {
  const t = Math.min(1, Math.max(0, progress));
  const deltaX = projectile.endX - projectile.startX;
  const deltaY = projectile.endY - projectile.startY;
  const groundX = projectile.startX + deltaX * t;
  const groundY = projectile.startY + deltaY * t;
  // Парабола: 0 на концах, 1 в середине
  const lift = 4 * t * (1 - t);

  return {
    groundX,
    groundY,
    x: groundX,
    // Вверх по экрану — меньше y
    y: groundY - projectile.arcHeight * lift,
    lift,
    // Производная положения: по горизонтали скорость постоянная, по вертикали
    // к ней добавляется подъем (первая половина) и спуск (вторая)
    angleRad: Math.atan2(deltaY - projectile.arcHeight * 4 * (1 - 2 * t), deltaX),
  };
};
